import type { Employee, PayrollRun, PayrollRunCreate, PayrollRunStatus } from '@erp/shared';
import { ArrowRight, Eye, ListChecks, Wallet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge, Button, Card, DataTable, type DataTableColumn, EmptyState, FormField, Input, Money, Skeleton,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { attendanceErrorCode, formatMinutes } from '../../lib/attendance.js';

import { PayrollSettingsCard } from './PayrollSettingsCard.js';

const STATUS_TONE: Record<PayrollRunStatus, 'neutral' | 'info' | 'success' | 'warning'> = {
  draft: 'neutral',
  approved: 'info',
  posted: 'success',
  cancelled: 'warning',
};

interface PayslipLine {
  lineNo: number;
  kind: 'earning' | 'deduction';
  code: string;
  description: string | null;
  amount: string;
  /** Days (ABSENCE) or minutes (LATE, OVERTIME, HOURS) behind an attendance line. */
  quantity?: string | null;
}

/** Lines whose quantity is minutes; ABSENCE's is days. */
const MINUTE_LINES = new Set(['LATE', 'OVERTIME', 'HOURS']);

interface RunDetail extends PayrollRun {
  payslips: Array<{
    id: string;
    employeeId: string;
    currencyCode: string;
    gross: string;
    deductionsTotal: string;
    net: string;
    fxRateToBase: string;
    baseGross: string;
    baseNet: string;
    lines: PayslipLine[];
  }>;
}

export function PayrollRunsPage(): JSX.Element {
  const { t, i18n } = useTranslation('hr');
  const canRun = usePermission('hr.payroll.run');
  const canAdmin = usePermission('hr.admin');
  const [items, setItems] = useState<PayrollRun[] | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<RunDetail | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: PayrollRun[] }>('/hr/payroll-runs');
    setItems(r.data.items);
  };

  useEffect(() => {
    void reload();
    // One-shot fetch of the employee directory so the payslip rows can
    // render code + display name instead of a UUID-prefix. Small data,
    // stable per session.
    void (async () => {
      try {
        const r = await api().get<{ items: Employee[] }>('/hr/employees');
        setEmployees(r.data.items);
      } catch {
        // Non-fatal: payslip rows fall back to the truncated UUID.
      }
    })();
  }, []);

  // Map keyed by employee id; built once whenever the directory loads.
  const employeeById = useMemo(() => {
    const m = new Map<string, Employee>();
    for (const e of employees) m.set(e.id, e);
    return m;
  }, [employees]);

  const openDetail = async (id: string): Promise<void> => {
    setActionError(null);
    const r = await api().get<RunDetail>(`/hr/payroll-runs/${id}`);
    setSelected(r.data);
  };

  const doAction = async (id: string, action: 'approve' | 'post' | 'cancel'): Promise<void> => {
    setActionError(null);
    try {
      await api().post(`/hr/payroll-runs/${id}/${action}`, {});
      await reload();
      await openDetail(id);
    } catch (err) {
      // The one refusal with a fix on this same page: say where it is.
      setActionError(attendanceErrorCode(err) === 'hr_gl_accounts_not_configured'
        ? t('payroll.errors.accounts')
        : t('payroll.errors.action'));
    }
  };

  if (!items) {
    return (
      <Card>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const columns: DataTableColumn<PayrollRun>[] = [
    {
      id: 'runNumber',
      header: t('payroll.columns.runNumber'),
      sortValue: (r) => r.runNumber,
      searchValue: (r) => r.runNumber,
      cellClassName: 'font-mono text-xs',
      cell: (r) => r.runNumber,
    },
    {
      id: 'period',
      header: t('payroll.columns.period'),
      sortValue: (r) => r.periodStart,
      searchValue: (r) => `${r.periodStart} ${r.periodEnd}`,
      cellClassName: 'font-mono text-xs',
      cell: (r) => (
        <span className="inline-flex items-center gap-1">
          {r.periodStart}
          <ArrowRight className="h-3 w-3 rtl:rotate-180" aria-hidden />
          {r.periodEnd}
        </span>
      ),
    },
    {
      id: 'payDate',
      header: t('payroll.columns.payDate'),
      sortValue: (r) => r.payDate,
      cellClassName: 'font-mono text-xs',
      cell: (r) => r.payDate,
    },
    {
      id: 'gross',
      header: t('payroll.columns.gross'),
      align: 'end',
      sortValue: (r) => Number(r.grossTotal),
      cell: (r) => (
        <Money amount={r.grossTotal} currency={r.baseCurrencyCode} locale={i18n.language} />
      ),
    },
    {
      id: 'net',
      header: t('payroll.columns.net'),
      align: 'end',
      sortValue: (r) => Number(r.netTotal),
      cell: (r) => (
        <Money amount={r.netTotal} currency={r.baseCurrencyCode} locale={i18n.language} />
      ),
    },
    {
      id: 'status',
      header: t('payroll.columns.status'),
      sortValue: (r) => t(`payroll.status.${r.status}`),
      searchValue: (r) => t(`payroll.status.${r.status}`),
      cell: (r) => <Badge tone={STATUS_TONE[r.status]}>{t(`payroll.status.${r.status}`)}</Badge>,
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      cell: (r) => (
        <Button variant="ghost" size="sm" onClick={() => { void openDetail(r.id); }} title={t('payroll.view')}>
          <Eye className="h-4 w-4" aria-hidden />
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('payroll.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('payroll.description')}</p>
        </div>
        {canRun && !showCreate && (
          <Button onClick={() => setShowCreate(true)}>{t('payroll.create.title')}</Button>
        )}
      </header>

      {canAdmin && <PayrollSettingsCard />}

      {showCreate && (
        <CreateRunForm
          onCancel={() => setShowCreate(false)}
          onCreated={async (created) => {
            setShowCreate(false);
            await reload();
            await openDetail(created.id);
          }}
        />
      )}

      {items.length === 0 && !showCreate ? (
        <EmptyState
          icon={<Wallet className="h-8 w-8" aria-hidden />}
          title={t('payroll.empty')}
          description={t('payroll.emptyDescription')}
          action={
            canRun ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                {t('payroll.create.title')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={items}
            rowKey={(r) => r.id}
            searchPlaceholder={t('payroll.columns.runNumber')}
          />
        </Card>
      )}

      {selected && (
        <RunDetailCard
          run={selected}
          locale={i18n.language}
          canRun={canRun}
          actionError={actionError}
          employeeById={employeeById}
          onClose={() => setSelected(null)}
          onAction={(action) => { void doAction(selected.id, action); }}
        />
      )}
    </div>
  );
}

interface DetailProps {
  run: RunDetail;
  locale: string;
  canRun: boolean;
  actionError: string | null;
  /** UUID-keyed lookup so the payslip rows can show employee code + name instead of a UUID prefix. */
  employeeById: Map<string, Employee>;
  onClose: () => void;
  onAction: (action: 'approve' | 'post' | 'cancel') => void;
}

type Payslip = RunDetail['payslips'][number];

function RunDetailCard({ run, locale, canRun, actionError, employeeById, onClose, onAction }: DetailProps): JSX.Element {
  const { t } = useTranslation('hr');
  const [slipId, setSlipId] = useState<string | null>(null);
  const slip = run.payslips.find((p) => p.id === slipId) ?? null;
  const slipEmployee = slip ? employeeById.get(slip.employeeId) : undefined;

  const payslipColumns: DataTableColumn<Payslip>[] = [
    {
      id: 'employeeId',
      header: t('payroll.payslip.employeeId'),
      sortValue: (p) => employeeById.get(p.employeeId)?.code ?? p.employeeId,
      searchValue: (p) => {
        const emp = employeeById.get(p.employeeId);
        return emp ? `${emp.code} ${emp.displayName}` : p.employeeId;
      },
      cellClassName: 'text-sm',
      cell: (p) => {
        const emp = employeeById.get(p.employeeId);
        return emp ? (
          <>
            <span className="font-mono text-xs text-fg-muted">{emp.code}</span>{' '}
            <span className="font-medium">{emp.displayName}</span>
          </>
        ) : (
          // Directory missing or fetch failed → fall back to a
          // short UUID prefix so the row still has *some* tag.
          <span className="font-mono text-xs text-fg-muted">{p.employeeId.slice(0, 8)}</span>
        );
      },
    },
    {
      id: 'currency',
      header: t('payroll.payslip.currency'),
      sortValue: (p) => p.currencyCode,
      searchValue: (p) => p.currencyCode,
      cellClassName: 'font-mono text-xs',
      cell: (p) => p.currencyCode,
    },
    {
      id: 'gross',
      header: t('payroll.payslip.gross'),
      align: 'end',
      sortValue: (p) => Number(p.gross),
      cell: (p) => <Money amount={p.gross} currency={p.currencyCode} locale={locale} />,
    },
    {
      id: 'deductions',
      header: t('payroll.payslip.deductions'),
      align: 'end',
      sortValue: (p) => Number(p.deductionsTotal),
      cell: (p) => <Money amount={p.deductionsTotal} currency={p.currencyCode} locale={locale} />,
    },
    {
      id: 'net',
      header: t('payroll.payslip.net'),
      align: 'end',
      sortValue: (p) => Number(p.net),
      cell: (p) => <Money amount={p.net} currency={p.currencyCode} locale={locale} />,
    },
    {
      id: 'baseNet',
      header: t('payroll.payslip.baseNet'),
      align: 'end',
      sortValue: (p) => Number(p.baseNet),
      cellClassName: 'text-fg-muted',
      cell: (p) => (
        <Money
          amount={p.baseNet}
          currency={run.baseCurrencyCode}
          locale={locale}
          approximate={p.currencyCode !== run.baseCurrencyCode}
        />
      ),
    },
    {
      id: 'lines',
      header: '',
      align: 'end',
      cell: (p) => (
        <Button
          variant={slipId === p.id ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setSlipId(slipId === p.id ? null : p.id)}
          title={t('payroll.lines.show')}
          aria-label={t('payroll.lines.show')}
        >
          <ListChecks className="h-4 w-4" aria-hidden />
        </Button>
      ),
    },
  ];

  const quantityText = (l: PayslipLine): string => {
    if (l.quantity === null || l.quantity === undefined) return '';
    const n = Number(l.quantity);
    return MINUTE_LINES.has(l.code) ? formatMinutes(n) : t('payroll.lines.days', { n: String(n) });
  };

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">{run.runNumber}</h2>
          <p className="text-sm text-fg-muted font-mono">
            <span className="inline-flex items-center gap-1">
              {run.periodStart}
              <ArrowRight className="h-3 w-3 rtl:rotate-180" aria-hidden />
              {run.periodEnd}
            </span>
            {' · '}{t('payroll.payDate')}: {run.payDate}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={STATUS_TONE[run.status]}>{t(`payroll.status.${run.status}`)}</Badge>
          <Button variant="ghost" size="sm" onClick={onClose}>{t('payroll.close')}</Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        <Stat label={t('payroll.columns.gross')} value={<Money amount={run.grossTotal} currency={run.baseCurrencyCode} locale={locale} />} />
        <Stat label={t('payroll.deductions')} value={<Money amount={run.deductionsTotal} currency={run.baseCurrencyCode} locale={locale} />} />
        <Stat label={t('payroll.columns.net')} value={<Money amount={run.netTotal} currency={run.baseCurrencyCode} locale={locale} />} />
      </div>

      {canRun && run.status !== 'cancelled' && run.status !== 'posted' && (
        <div className="mt-4 flex flex-wrap gap-2">
          {run.status === 'draft' && (
            <Button onClick={() => onAction('approve')}>{t('payroll.approve')}</Button>
          )}
          {run.status === 'approved' && (
            <Button onClick={() => onAction('post')}>{t('payroll.post')}</Button>
          )}
          <Button variant="danger" onClick={() => onAction('cancel')}>{t('payroll.cancel')}</Button>
        </div>
      )}

      {actionError && <p className="mt-3 text-sm text-danger" role="alert">{actionError}</p>}

      <div className="mt-6">
        <h3 className="text-sm font-semibold text-fg-muted">{t('payroll.payslips')} ({run.payslips.length})</h3>
        <Card className="mt-2 overflow-hidden p-0">
          <DataTable
            columns={payslipColumns}
            data={run.payslips}
            rowKey={(p) => p.id}
            searchPlaceholder={t('payroll.payslip.employeeId')}
          />
        </Card>
      </div>

      {slip && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-fg-muted">
            {t('payroll.lines.title')} — {slipEmployee ? `${slipEmployee.code} ${slipEmployee.displayName}` : slip.employeeId.slice(0, 8)}
          </h3>
          <Card className="mt-2 p-0">
            <table className="w-full text-sm">
              <tbody>
                {slip.lines.map((l) => (
                  <tr key={l.lineNo} className="border-b border-border last:border-0">
                    <td className="px-4 py-2">{t(`payroll.lines.codes.${l.code}`, { defaultValue: l.description ?? l.code })}</td>
                    <td className="px-4 py-2 font-mono text-xs text-fg-muted tabular-nums">{quantityText(l)}</td>
                    <td className={`px-4 py-2 text-end ${l.kind === 'deduction' || Number(l.amount) < 0 ? 'text-danger' : ''}`}>
                      <Money
                        amount={l.kind === 'deduction' ? String(-Math.abs(Number(l.amount))) : l.amount}
                        currency={slip.currencyCode}
                        locale={locale}
                      />
                    </td>
                  </tr>
                ))}
                <tr className="bg-bg-subtle font-semibold">
                  <td className="px-4 py-2" colSpan={2}>{t('payroll.payslip.net')}</td>
                  <td className="px-4 py-2 text-end"><Money amount={slip.net} currency={slip.currencyCode} locale={locale} /></td>
                </tr>
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="rounded-md border border-border bg-bg-subtle px-4 py-3">
      <div className="text-xs text-fg-muted">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}

interface CreateFormProps {
  onCancel: () => void;
  onCreated: (created: PayrollRun) => Promise<void>;
}

function CreateRunForm({ onCancel, onCreated }: CreateFormProps): JSX.Element {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';
  const [form, setForm] = useState<PayrollRunCreate>({
    periodStart: firstOfMonth,
    periodEnd: today,
    payDate: today,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const r = await api().post<PayrollRun>('/hr/payroll-runs', form);
      await onCreated(r.data);
    } catch {
      setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <h2 className="mb-4 text-lg font-semibold">{t('payroll.create.title')}</h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-3" noValidate>
        <FormField id="r-start" label={t('payroll.create.periodStart')} required>
          <Input id="r-start" type="date" value={form.periodStart} onChange={(e) => setForm({ ...form, periodStart: e.target.value })} required />
        </FormField>
        <FormField id="r-end" label={t('payroll.create.periodEnd')} required>
          <Input id="r-end" type="date" value={form.periodEnd} onChange={(e) => setForm({ ...form, periodEnd: e.target.value })} required />
        </FormField>
        <FormField id="r-pay" label={t('payroll.create.payDate')} required>
          <Input id="r-pay" type="date" value={form.payDate} onChange={(e) => setForm({ ...form, payDate: e.target.value })} required />
        </FormField>
        {error && <p className="md:col-span-3 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-3 flex gap-2">
          <Button type="submit" loading={saving}>{t('payroll.create.submit')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}
